import React, { useState, useEffect } from 'react';
import { useUser } from 'context/user/User';
import styled, { keyframes } from 'styled-components';
import { getInventoryItems, deleteInventoryItem } from 'api/requests/inventory';
import InventoryItemModal from './modals/InventoryItemModal';
import InventoryTransactionModal from './modals/InventoryTransactionModal';
import useToast from 'hooks/useToast';
import ToastContainer from 'components/_UI/ToastsContainer';
import InfoTooltip from 'components/_UI/InfoTooltip';
import Modal from 'components/_UI/Modal';
import colors from 'theme/colors';
import { Package, Plus, Edit2, Trash2, ArrowRightLeft, RefreshCw, AlertCircle, Link2 } from 'lucide-react';
import ConfirmDialog from 'modules/restaurant/menu/components/ConfirmDialog';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

export default function RestaurantInventoryPage() {
    const { user } = useUser();
    const slug = user?.establishments?.[0]?.slug;
    const { toasts, toast } = useToast();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isItemModalOpen, setIsItemModalOpen] = useState(false);
    const [itemToEdit, setItemToEdit] = useState(null);
    const [isTxModalOpen, setIsTxModalOpen] = useState(false);
    const [itemToMove, setItemToMove] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null, name: '' });
    const [deleting, setDeleting] = useState(false);
    const [showOnlyLinked, setShowOnlyLinked] = useState(false);
    const [linkedModalItem, setLinkedModalItem] = useState(null);

    useEffect(() => {
        fetchItems();
    }, [slug]);

    const fetchItems = async () => {
        if (!slug) return;
        try {
            setLoading(true);
            const data = await getInventoryItems(slug);
            setItems(data);
        } catch (error) {
            console.error('Error fetching inventory items', error);
            toast.error('Erro ao carregar insumos.');
        } finally {
            setLoading(false);
        }
    };

    const confirmDeletion = (item) => {
        setConfirmDelete({ open: true, id: item.id, name: item.name });
    };

    const handleDelete = async () => {
        try {
            setDeleting(true);
            await deleteInventoryItem(slug, confirmDelete.id);
            fetchItems();
            toast.success('Insumo removido com sucesso!');
        } catch (error) {
            console.error('Error deleting item', error);
            toast.error('Erro ao remover o insumo.');
        } finally {
            setDeleting(false);
            setConfirmDelete({ open: false, id: null, name: '' });
        }
    };

    const getStatus = (item) => {
        if (Number(item.current_quantity) === 0) return 'zerado';
        if (item.min_stock_alert && Number(item.current_quantity) <= Number(item.min_stock_alert)) return 'baixo';
        return 'normal';
    };

    const formatPrice = (value) => {
        return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    };

    const getLinkedItems = (item) => {
        const products = (item.product_tech_sheets || []).map(t => ({
            key: `product-${t.id}`,
            label: t.menu_product?.name || 'Produto removido',
            quantity_used: t.quantity_used,
        }));
        const options = (item.option_tech_sheets || []).map(t => ({
            key: `option-${t.id}`,
            label: `${t.menu_option?.name || 'Adicional removido'} (adicional)`,
            quantity_used: t.quantity_used,
        }));
        return [...products, ...options];
    };

    const formatLinkedItem = (item, linked) => {
        return `${linked.label} — ${Number(linked.quantity_used)}${item.unit_of_measure}`;
    };

    const VISIBLE_LINKED_PRODUCTS = 3;

    const displayedItems = showOnlyLinked
        ? items.filter(item => getLinkedItems(item).length > 0)
        : items;

    const handleOpenItemModal = (item = null) => {
        setItemToEdit(item);
        setIsItemModalOpen(true);
    };

    const handleOpenTxModal = (item) => {
        setItemToMove(item);
        setIsTxModalOpen(true);
    };

    if (!slug) {
        return (
            <PageWrapper>
                <NoEstablishmentState>
                    <NoEstablishmentIcon><Package size={36} color='#999' /></NoEstablishmentIcon>
                    <NoEstablishmentTitle>Nenhum estabelecimento encontrado</NoEstablishmentTitle>
                    <NoEstablishmentHint>Crie um estabelecimento para gerenciar seu estoque.</NoEstablishmentHint>
                </NoEstablishmentState>
            </PageWrapper>
        );
    }

    return (
        <PageWrapper>
            <ToastContainer toasts={toasts} />
            
            <PageHeader>
                <HeaderLeft>
                    <HeaderIcon><Package size={20} color={panelAccent(colors.occasionalPurple)} /></HeaderIcon>
                    <div>
                        <PageTitle>Estoque</PageTitle>
                        <PageSubtitle>Gerencie os insumos, compras e valores do seu estabelecimento</PageSubtitle>
                    </div>
                </HeaderLeft>
                <HeaderActions>
                    <FilterToggleBtn
                        type='button'
                        $active={showOnlyLinked}
                        onClick={() => setShowOnlyLinked(v => !v)}
                        aria-pressed={showOnlyLinked}
                    >
                        <Link2 size={14} />
                        Vinculados ao cardápio
                    </FilterToggleBtn>
                    <NewBtn onClick={() => handleOpenItemModal()} aria-label='Novo Insumo'>
                        <Plus size={20} color='#fff' strokeWidth={2.5} />
                    </NewBtn>
                </HeaderActions>
            </PageHeader>

            <TableCard>
                <ContentWrapper>
                    {loading ? (
                        <LoadingContainer>
                            <Spinner />
                            <LoadingText>Carregando estoque...</LoadingText>
                        </LoadingContainer>
                    ) : items.length === 0 ? (
                        <EmptyState>
                            <EmptyIconWrapper><Package size={32} color={panelAccent(colors.occasionalPurple)} /></EmptyIconWrapper>
                            <EmptyText>Nenhum insumo cadastrado</EmptyText>
                            <EmptyHint>Adicione insumos para controlar os custos dos seus produtos</EmptyHint>
                            <EmptyCTA onClick={() => handleOpenItemModal()}>
                                <Plus size={16} /> Cadastrar Primeiro Insumo
                            </EmptyCTA>
                        </EmptyState>
                    ) : displayedItems.length === 0 ? (
                        <EmptyState>
                            <EmptyIconWrapper><Link2 size={32} color={panelAccent(colors.occasionalPurple)} /></EmptyIconWrapper>
                            <EmptyText>Nenhum insumo vinculado a produtos</EmptyText>
                            <EmptyHint>Edite um insumo e adicione produtos do cardápio que o utilizam.</EmptyHint>
                        </EmptyState>
                    ) : (
                        <StyledTableContainer>
                            <StyledTable>
                                <thead>
                                    <tr>
                                        <th style={{ width: '30%' }}>Insumo</th>
                                        <th style={{ width: '13%' }}>Qtd. Atual</th>
                                        <th style={{ width: '13%' }}>Custo Médio</th>
                                        <th style={{ width: '15%' }}>Valor em Estoque</th>
                                        <th style={{ width: '13%' }}>Status</th>
                                        <th style={{ width: '16%', textAlign: 'right' }}>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayedItems.map(item => {
                                        const status = getStatus(item);
                                        const linkedItems = getLinkedItems(item);
                                        return (
                                            <tr key={item.id}>
                                                <td>
                                                    <ItemName>{item.name}</ItemName>
                                                    {item.min_stock_alert > 0 && (
                                                        <ItemSub>Alerta: {item.min_stock_alert} {item.unit_of_measure}</ItemSub>
                                                    )}
                                                    {linkedItems.length > 0 && (
                                                        <LinkedProductsRow>
                                                            {linkedItems.slice(0, VISIBLE_LINKED_PRODUCTS).map(linked => (
                                                                <ProductTag key={linked.key}>
                                                                    {formatLinkedItem(item, linked)}
                                                                </ProductTag>
                                                            ))}
                                                            {linkedItems.length > VISIBLE_LINKED_PRODUCTS && (
                                                                <SeeMoreBtn type='button' onClick={() => setLinkedModalItem(item)}>
                                                                    +{linkedItems.length - VISIBLE_LINKED_PRODUCTS} ver mais
                                                                </SeeMoreBtn>
                                                            )}
                                                        </LinkedProductsRow>
                                                    )}
                                                </td>
                                                <td>
                                                    <ItemVal>
                                                        {Number(item.current_quantity).toFixed(2)} {item.unit_of_measure}
                                                    </ItemVal>
                                                </td>
                                                <td>
                                                    <ItemVal>{formatPrice(item.average_unit_cost)}</ItemVal>
                                                    <ItemSub>por {item.unit_of_measure}</ItemSub>
                                                </td>
                                                <td>
                                                    <ItemVal>{formatPrice(item.total_value_in_stock)}</ItemVal>
                                                </td>
                                                <td>
                                                    <StatusBadge status={status}>
                                                        {status === 'zerado' && <AlertCircle size={12} />}
                                                        {status.toUpperCase()}
                                                    </StatusBadge>
                                                </td>
                                                <td>
                                                    <ActionsWrapper>
                                                        <InfoTooltip type='none' bgColor={colors.night} text='Registrar uma entrada ou saída de estoque para este insumo'>
                                                            <ActionBtn onClick={() => handleOpenTxModal(item)} aria-label='Movimentar'>
                                                                <ArrowRightLeft size={16} color={colors.nightDark} />
                                                            </ActionBtn>
                                                        </InfoTooltip>
                                                        <InfoTooltip type='none' bgColor={colors.night} text='Editar dados do insumo e os produtos do cardápio vinculados a ele'>
                                                            <ActionBtn onClick={() => handleOpenItemModal(item)} aria-label='Editar'>
                                                                <Edit2 size={16} color={colors.nightDark} />
                                                            </ActionBtn>
                                                        </InfoTooltip>
                                                        <InfoTooltip type='none' bgColor={colors.night} text='Remover este insumo e todo o seu histórico de transações'>
                                                            <ActionBtn onClick={() => confirmDeletion(item)} aria-label='Remover' className="danger">
                                                                <Trash2 size={16} color={colors.danger} />
                                                            </ActionBtn>
                                                        </InfoTooltip>
                                                    </ActionsWrapper>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </StyledTable>
                        </StyledTableContainer>
                    )}
                </ContentWrapper>
            </TableCard>

            <InventoryItemModal
                isOpen={isItemModalOpen}
                onClose={() => setIsItemModalOpen(false)}
                establishmentSlug={slug}
                itemToEdit={itemToEdit}
                onSuccess={fetchItems}
                toast={toast}
            />

            <InventoryTransactionModal
                isOpen={isTxModalOpen}
                onClose={() => setIsTxModalOpen(false)}
                establishmentSlug={slug}
                item={itemToMove}
                onSuccess={fetchItems}
                toast={toast}
            />

            <ConfirmDialog
                isOpen={confirmDelete.open}
                title='Remover insumo'
                message={`Tem certeza que deseja remover "${confirmDelete.name}"? Esta ação removerá também o histórico de transações deste insumo e não pode ser desfeita.`}
                confirmLabel='Remover'
                danger
                loading={deleting}
                onConfirm={handleDelete}
                onCancel={() => setConfirmDelete({ open: false, id: null, name: '' })}
            />

            <Modal
                isOpen={!!linkedModalItem}
                onClose={() => setLinkedModalItem(null)}
                title={linkedModalItem ? `Produtos e adicionais vinculados — ${linkedModalItem.name}` : ''}
                size='medium'
            >
                {linkedModalItem && (
                    <LinkedModalList>
                        {getLinkedItems(linkedModalItem).map(linked => (
                            <LinkedModalRow key={linked.key}>
                                <span>{linked.label}</span>
                                <strong>{Number(linked.quantity_used)}{linkedModalItem.unit_of_measure}</strong>
                            </LinkedModalRow>
                        ))}
                    </LinkedModalList>
                )}
            </Modal>
        </PageWrapper>
    );
}

// Styled Components (following PosEventsPage standards)
const PageWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    background: ${colors.neutralBackground};
`;

const PageHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 16px 14px;
    border-radius: 14px 14px 0 0;
    background: ${colors.neutralLight};
    flex-shrink: 0;
`;

const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const HeaderIcon = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: ${panelAccentAlpha(colors.occasionalPurple, '10')};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

const PageTitle = styled.h1`
    font-size: 18px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0;
    line-height: 1.3;
`;

const PageSubtitle = styled.p`
    font-size: 13px;
    color: #666;
    margin: 2px 0 0;
`;

const NewBtn = styled.button`
    width: 44px;
    height: 44px;
    border-radius: 14px;
    background: ${panelAccent(colors.occasionalPurple)};
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    transition: transform 0.15s, box-shadow 0.15s;
    &:hover { transform: scale(1.06); box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2); }
    &:active { transform: scale(0.96); }
    &:focus-visible { outline: 2px solid ${panelAccent(colors.occasionalPurple)}; outline-offset: 3px; }
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const FilterToggleBtn = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 0 14px;
    height: 38px;
    border-radius: 12px;
    border: 1.5px solid ${({ $active }) => $active ? panelAccent(colors.occasionalPurple) : colors.neutralDark};
    background: ${({ $active }) => $active ? `${panelAccentAlpha(colors.occasionalPurple, '12')}` : '#fff'};
    color: ${({ $active }) => $active ? panelAccent(colors.occasionalPurple) : '#666'};
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.15s;
    &:hover { border-color: ${panelAccent(colors.occasionalPurple)}; color: ${panelAccent(colors.occasionalPurple)}; }
    &:focus-visible { outline: 2px solid ${panelAccent(colors.occasionalPurple)}; outline-offset: 2px; }
`;

const TableCard = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: 0 0 14px 14px;
    box-shadow: 0 -1px 4px rgba(0, 0, 0, 0.04), 0 2px 12px rgba(0, 0, 0, 0.06);
    overflow: hidden;
    min-height: 0;
`;

const ContentWrapper = styled.div`
    padding: 20px 16px 24px;
    flex: 1;
    overflow-y: auto;
`;

const StyledTableContainer = styled.div`
    overflow-x: auto;
    border-radius: 12px;
    border: 1px solid ${colors.neutralDark};
    background: #fff;
`;

const StyledTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    min-width: 700px;
    table-layout: fixed;

    th, td {
        padding: 16px;
        text-align: left;
        border-bottom: 1px solid ${colors.neutralDark};
        overflow-wrap: break-word;
    }
    
    th {
        font-size: 13px;
        font-weight: 600;
        color: #666;
        text-transform: uppercase;
        background: #fafafa;
        letter-spacing: 0.5px;
    }

    tr:last-child td {
        border-bottom: none;
    }

    tbody tr {
        transition: background-color 0.15s;
        &:hover {
            background-color: #fcfcfc;
        }
    }
`;

const ItemName = styled.div`
    font-size: 15px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const ItemSub = styled.div`
    font-size: 12px;
    color: #888;
    margin-top: 2px;
`;

const LinkedProductsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 5px;
    margin-top: 6px;
`;

const ProductTag = styled.span`
    display: inline-flex;
    align-items: center;
    font-size: 11px;
    font-weight: 600;
    color: ${panelAccent(colors.occasionalPurple)};
    background: ${panelAccentAlpha(colors.occasionalPurple, '0A')};
    border: 1px solid ${panelAccentAlpha(colors.occasionalPurple, '40')};
    border-radius: 6px;
    padding: 3px 8px;
    max-width: 100%;
    overflow-wrap: break-word;
`;

const SeeMoreBtn = styled.button`
    font-size: 11px;
    font-weight: 600;
    color: ${colors.night};
    background: ${colors.neutralDark};
    border: 1px solid ${colors.neutralDark};
    border-radius: 6px;
    padding: 3px 8px;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s;
    &:hover { background: #e0e0e5; }
`;

const LinkedModalList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const LinkedModalRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 14px;
    border-radius: 10px;
    background: ${colors.neutralBackground || '#f9f9f9'};
    border: 1px solid ${colors.neutralDark};
    font-size: 14px;
    color: ${colors.nightDark};

    strong {
        color: ${panelAccent(colors.occasionalPurple)};
        font-weight: 600;
        flex-shrink: 0;
    }
`;

const ItemVal = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const StatusBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.3px;
    
    background-color: ${props => 
        props.status === 'zerado' ? '#fee2e2' : 
        props.status === 'baixo' ? '#fef3c7' : 
        '#dcfce7'};
        
    color: ${props => 
        props.status === 'zerado' ? '#b91c1c' : 
        props.status === 'baixo' ? '#b45309' : 
        '#15803d'};
`;

const ActionsWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
`;

const ActionBtn = styled.button`
    background: transparent;
    border: none;
    border-radius: 8px;
    padding: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s;

    &:hover {
        background: ${colors.neutralDark};
    }
    
    &.danger:hover {
        background: ${colors.danger}15;
    }
`;

// States
const NoEstablishmentState = styled.div`
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    height: 100%; padding: 40px 20px; text-align: center;
`;

const NoEstablishmentIcon = styled.div`
    width: 64px; height: 64px; border-radius: 50%; background: ${colors.neutralDark};
    display: flex; align-items: center; justify-content: center; margin-bottom: 16px;
`;

const NoEstablishmentTitle = styled.h2`
    font-size: 17px; font-weight: 600; color: ${colors.nightDark}; margin: 0 0 6px;
`;

const NoEstablishmentHint = styled.p`
    font-size: 14px; color: #666; margin: 0; max-width: 280px;
`;

const EmptyState = styled.div`
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    padding: 60px 20px; text-align: center;
`;

const EmptyIconWrapper = styled.div`
    width: 56px; height: 56px; border-radius: 50%; background: ${panelAccentAlpha(colors.occasionalPurple, '10')};
    display: flex; align-items: center; justify-content: center; margin-bottom: 14px;
`;

const EmptyText = styled.p`font-weight: 600; font-size: 16px; color: ${colors.nightDark}; margin: 0 0 6px;`;
const EmptyHint = styled.p`font-size: 13px; color: #666; margin: 0 0 18px;`;

const EmptyCTA = styled.button`
    display: flex; align-items: center; gap: 8px; padding: 12px 24px; border-radius: 10px;
    background: ${panelAccent(colors.occasionalPurple)}; color: #fff; font-size: 14px; font-weight: 600;
    border: none; cursor: pointer; transition: opacity 0.15s;
    &:hover { opacity: 0.9; }
`;

const LoadingContainer = styled.div`
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    height: 200px; gap: 12px;
`;

const LoadingText = styled.span`
    font-size: 13px; color: #666;
`;

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
const Spinner = styled.div`
    width: 32px; height: 32px; border: 3px solid ${colors.neutralDark};
    border-top-color: ${panelAccent(colors.occasionalPurple)}; border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
`;
